import React, { createContext, useContext, useEffect, useState, useRef, useMemo } from 'react'
import { supabase } from '@/lib/supabase'
import { User } from '@/api/entities'
import { setUserId, setUserProperties } from '@/utils/analytics'
import { linkUTMToUser, generateSessionId } from '@/utils/utmTracking'
import { clearUserCache, setOnSessionInvalid } from '@/utils/authCache'

// Small in-memory coalescer to prevent parallel getSession calls
const inFlight = { session: null }
const getSessionOnce = async () => {
  if (!inFlight.session) {
    inFlight.session = supabase.auth.getSession().then((res) => res.data.session).finally(() => {
      inFlight.session = null
    })
  }
  return inFlight.session
}

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const linkingUTMRef = useRef(false) // Prevent duplicate UTM linking calls
  const initializedRealtime = useRef(false) // Track if realtime auth has been set
  const getSessionInProgressRef = useRef(false) // Prevent multiple simultaneous getSession calls and onAuthStateChange from running during initial session load

  useEffect(() => {
    let mounted = true

    // Get initial session with coalescing
    const getInitialSession = async () => {
      // Prevent multiple simultaneous calls
      if (getSessionInProgressRef.current) {
        console.log('[AuthContext] getSession already in progress, skipping...')
        return
      }
      
      try {
        getSessionInProgressRef.current = true
        const pathname = window.location.pathname
        // Skip session check for OAuth callback routes and public pages
        if (pathname.includes('/auth/google/callback') || 
            pathname.includes('/auth/test-callback') ||
            pathname === '/terms' ||
            pathname === '/privacy' ||
            pathname === '/help' ||
            pathname === '/faq' ||
            pathname.startsWith('/blog') ||
            pathname === '/Premium' ||
            pathname === '/premium-stripe-test' ||
            pathname === '/landing' ||
            pathname === '/') {
          console.log('Skipping initial session check for public route:', pathname)
          setLoading(false)
          getSessionInProgressRef.current = false
          return
        }
        
        // Use coalesced getSession to prevent parallel calls
        const s = await getSessionOnce()
        if (!mounted) {
          getSessionInProgressRef.current = false
          return
        }
        
        setSession(s ?? null)
        setUser(s?.user || null)
        setLoading(false)

        // Set Realtime auth once per session to avoid repeated auth requests
        if (s?.access_token && !initializedRealtime.current) {
          await supabase.realtime.setAuth(s.access_token)
          initializedRealtime.current = true
        }
        
        // Reset ref after successful initial session load
        getSessionInProgressRef.current = false
      } catch (error) {
        console.error('Error in getInitialSession:', error)
        setLoading(false)
        // Reset ref in error case as well
        getSessionInProgressRef.current = false
      }
    }

    getInitialSession()

    // When auth cache detects invalid session (e.g. refresh_token_not_found), sign out and redirect
    setOnSessionInvalid(() => {
      clearUserCache()
      initializedRealtime.current = false
      setSession(null)
      setUser(null)
      setLoading(false)
      const currentPath = window.location.pathname
      const isAuthPage = currentPath === '/auth/login' || currentPath === '/auth/signup' || currentPath === '/auth/reset-password'
      if (!isAuthPage && currentPath !== '/landing' && currentPath !== '/') {
        window.location.href = '/auth/login'
      }
    })

    // Listen for auth changes with error handling and debouncing
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        // Skip auth state changes during initial session load to prevent duplicate work
        if (getSessionInProgressRef.current) {
          console.log('[AuthContext] Skipping auth state change - getSession in progress')
          return
        }
        
        const pathname = window.location.pathname
        // Skip auth state changes for OAuth callback routes and public pages
        if (pathname.includes('/auth/google/callback') || 
            pathname.includes('/auth/test-callback') ||
            pathname === '/terms' ||
            pathname === '/privacy' ||
            pathname === '/help' ||
            pathname === '/faq' ||
            pathname.startsWith('/blog') ||
            pathname === '/Premium' ||
            pathname === '/premium-stripe-test' ||
            pathname === '/landing' ||
            pathname === '/') {
          console.log('Skipping auth state change for public route:', pathname)
          return
        }
        
        console.log('Auth state changed:', event, newSession?.user?.email)
        
        // Handle sign out immediately - clear state before any other logic
        if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
          clearUserCache()
          initializedRealtime.current = false
          setSession(null)
          setUser(null)
          setLoading(false)
          
          // Redirect to login page immediately
          // Only redirect if not already on login/signup pages to avoid redirect loops
          const currentPath = window.location.pathname
          const isAuthPage = currentPath === '/auth/login' || 
                            currentPath === '/auth/signup' || 
                            currentPath === '/auth/reset-password'
          
          // Only redirect if we're on a protected page (not already on an auth page)
          if (!isAuthPage && currentPath !== '/landing' && currentPath !== '/') {
            // Use window.location.href for reliable redirect that clears app state
            // Don't wait - redirect immediately
            window.location.href = '/auth/login'
            return // Exit early to prevent any other state updates
          }
          return // Exit early if already on auth page
        }
        
        // For non-sign-out events, update session and user normally
        setSession(newSession ?? null)
        setUser(newSession?.user || null)
        setLoading(false)

        // Set Realtime auth when session changes to avoid repeated auth requests
        if (newSession?.access_token) {
          await supabase.realtime.setAuth(newSession.access_token)
          initializedRealtime.current = true
        }
        
        // Invalidate auth cache on token refresh so next getCurrentUser() refetches
        if (event === 'TOKEN_REFRESHED') {
          clearUserCache()
        }
        
        // Set analytics user ID and properties when user logs in
        if (newSession?.user && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
          try {
            // Set user ID (use hashed/anonymized ID for privacy)
            setUserId(newSession.user.id)
            
            // Set initial user properties
            const userProperties = {
              account_created_date: newSession.user.created_at,
              email_domain: newSession.user.email?.split('@')[1] || 'unknown',
            }
            
            // Add subscription tier if available in metadata
            if (newSession.user.user_metadata?.subscription_tier) {
              userProperties.subscription_tier = newSession.user.user_metadata.subscription_tier
            }
            
            setUserProperties(userProperties)
            
            // Link any stored anonymous UTM data to this user account
            // This handles conversion attribution for users who visited with UTM params before signing up
            // Use ref to prevent duplicate calls during rapid auth state changes
            if (event === 'SIGNED_IN' && !linkingUTMRef.current) {
              linkingUTMRef.current = true
              try {
                const sessionId = generateSessionId()
                await linkUTMToUser(newSession.user.id, sessionId)
              } catch (utmError) {
                console.error('Error linking UTM data to user:', utmError)
                // Don't block auth flow if UTM linking fails
              } finally {
                // Reset flag after a delay to allow legitimate future sign-ins
                setTimeout(() => {
                  linkingUTMRef.current = false
                }, 5000)
              }
            }
          } catch (error) {
            console.error('Error setting analytics user properties:', error)
          }
        }
      }
    )

    return () => {
      mounted = false
      setOnSessionInvalid(null)
      subscription?.unsubscribe()
    }
  }, [])

  // Sign up with email and password
  const signUp = async (email, password, userData = {}) => {
    try {
      setLoading(true)
      const data = await User.signUp(email, password, userData)
      return { data, error: null }
    } catch (error) {
      console.error('Sign up error:', error)
      return { data: null, error }
    } finally {
      setLoading(false)
    }
  }

  // Sign in with email and password - simplified to reduce excessive requests
  const signIn = async (email, password) => {
    try {
      setLoading(true)
      
      // Simplified sign-in - remove complex timeout/retry logic that causes multiple requests
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })
      
      if (error) {
        console.error('[AuthContext] Sign in error:', error)
        return { data: null, error }
      }
      
      // Session is automatically set by onAuthStateChange listener
      // No need to manually call getSession() which causes additional requests
      return { data, error: null }
    } catch (error) {
      console.error('[AuthContext] Sign in exception:', error)
      return { data: null, error }
    } finally {
      setLoading(false)
    }
  }

  // Sign out
  const signOut = async () => {
    try {
      // Clear user state immediately to trigger ProtectedRoute redirect
      // Set loading to false so ProtectedRoute doesn't show spinner
      setUser(null)
      setSession(null)
      setLoading(false)
      clearUserCache() // Clear auth cache on sign out
      initializedRealtime.current = false
      
      // Perform the actual sign out asynchronously (don't await if we're redirecting)
      supabase.auth.signOut().catch(error => {
        console.error('Supabase sign out error:', error)
      })
      
      return { error: null }
    } catch (error) {
      console.error('Sign out error:', error)
      // Even on error, clear user state to allow redirect
      setUser(null)
      setSession(null)
      setLoading(false)
      return { error }
    }
  }

  // Reset password
  const resetPassword = async (email) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`
      })
      
      if (error) throw error
      return { error: null }
    } catch (error) {
      console.error('Reset password error:', error)
      return { error }
    }
  }

  // Update password
  const updatePassword = async (password) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password
      })
      
      if (error) throw error
      
      // Clear cache after successful password update
      clearUserCache()
      
      return { error: null }
    } catch (error) {
      console.error('Update password error:', error)
      return { error }
    }
  }

  // Update user profile
  const updateProfile = async (updates) => {
    try {
      const { error } = await supabase.auth.updateUser({
        data: updates
      })
      
      if (error) throw error
      
      // Clear cache after successful profile update
      clearUserCache()
      
      return { error: null }
    } catch (error) {
      console.error('Update profile error:', error)
      return { error }
    }
  }

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      user,
      session,
      loading,
      signUp,
      signIn,
      signOut,
      resetPassword,
      updatePassword,
      updateProfile
    }),
    [user, session, loading]
  )

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
